import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookletDesignerPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [booklets, setBooklets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    week_number: 1,
    focus_subjects: [],
    focus_topics: '',
    total_hours: 10
  });

  const subjectOptions = ['Physics', 'Mathematics', 'Chemistry', 'Biology'];

  useEffect(() => {
    fetchBooklets();
  }, []);

  const fetchBooklets = async () => {
    try {
      const response = await axios.get(`${API}/questions/booklets/`, {
        withCredentials: true
      });
      setBooklets(response.data.booklets || []);
    } catch (error) {
      console.error('Error fetching booklets:', error);
    }
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      focus_subjects: prev.focus_subjects.includes(subject)
        ? prev.focus_subjects.filter(s => s !== subject)
        : [...prev.focus_subjects, subject]
    }));
  };

  const handleCreateBooklet = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/questions/booklets/design`,
        {
          ...formData,
          focus_topics: formData.focus_topics.split(',').map(t => t.trim()).filter(Boolean)
        },
        { withCredentials: true }
      );

      alert(t('create_plan') + ' ✓');
      fetchBooklets();
      
      // Reset form
      setFormData({
        week_number: formData.week_number + 1,
        focus_subjects: [],
        focus_topics: '',
        total_hours: 10
      });
    } catch (error) {
      console.error('Error creating booklet:', error);
      alert(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Kitapçık Tasarımcısı' : 'Booklet Designer'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Create Booklet Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">📚</span>
              {language === 'tr' ? 'Yeni Kitapçık Oluştur' : 'Create New Booklet'}
            </h2>

            <form onSubmit={handleCreateBooklet} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('week_number')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.week_number}
                  onChange={(e) => setFormData({ ...formData, week_number: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('focus_subjects')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {subjectOptions.map(subject => (
                    <button
                      key={subject}
                      type="button"
                      onClick={() => handleSubjectToggle(subject)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.focus_subjects.includes(subject)
                          ? 'border-blue-600 bg-blue-50 text-blue-800'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('focus_topics')}
                </label>
                <textarea
                  value={formData.focus_topics}
                  onChange={(e) => setFormData({ ...formData, focus_topics: e.target.value })}
                  placeholder="Newton's Laws, Kinematics, ..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('weekly_hours')}
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="5"
                    max="40"
                    value={formData.total_hours}
                    onChange={(e) => setFormData({ ...formData, total_hours: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold text-gray-800 w-16 text-right">
                    {formData.total_hours} {t('hours')}
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || formData.focus_subjects.length === 0}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 transition-all duration-300 font-semibold"
              >
                {loading ? t('loading') : t('create_plan')}
              </button>
            </form>
          </div>

          {/* Booklets List */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">📖</span>
              {language === 'tr' ? 'Kitapçıklarım' : 'My Booklets'}
            </h2>

            {booklets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p>{t('no_booklets')}</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {booklets.map((booklet) => (
                  <button
                    key={booklet.id}
                    onClick={() => navigate(`/booklets/${booklet.id}`)}
                    className="w-full bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 hover:shadow-lg transition-all text-left"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-gray-800">{booklet.title}</h3>
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                        {t('week')} {booklet.week_number}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>📝 {booklet.total_questions} {t('questions')}</span>
                      <span>⏱️ {booklet.estimated_time_minutes} {t('minutes')}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookletDesignerPage;
